/**
 * Product Factory
 *
 * Factory pattern for dependency injection following clean architecture principles.
 * Creates and manages use case instances with proper dependency injection.
 *
 * @module productFactory
 */

import {CreateProductUseCase} from '../useCases/CreateProductUseCase';
import {UpdateProductUseCase} from '../useCases/UpdateProductUseCase';
import {DeleteProductUseCase} from '../useCases/DeleteProductUseCase';
import {GetProductByIdUseCase} from '../useCases/GetProductByIdUseCase';
import {ValidateProductFormUseCase} from '../useCases/ValidateProductFormUseCase';
import {GetAllProductsUseCase} from '../useCases/GetAllProductsUseCase';
import {GetAllBrandsUseCase} from '../useCases/GetAllBrandsUseCase';
import {GetAllTagsUseCase} from '../useCases/GetAllTagsUseCase';
import {GetAllProductTypesUseCase} from '../useCases/GetAllProductTypesUseCase';
import {ProductRepository} from '../repository/ProductRepository';
import {ProductRepositoryImpl} from '../repository/ProductRepositoryImpl';
import {ProductAPIDataSourceCombined} from '../api';

class ProductFactory {
  private static instance: ProductFactory;
  private productRepository: ProductRepository | null = null;
  private useCases: {
    createProduct: CreateProductUseCase | null;
    updateProduct: UpdateProductUseCase | null;
    deleteProduct: DeleteProductUseCase | null;
    getProductById: GetProductByIdUseCase | null;
    validateProductForm: ValidateProductFormUseCase | null;
    getAllProducts: GetAllProductsUseCase | null;
    getAllBrands: GetAllBrandsUseCase | null;
    getAllTags: GetAllTagsUseCase | null;
    getAllProductTypes: GetAllProductTypesUseCase | null;
  } = {
    createProduct: null,
    updateProduct: null,
    deleteProduct: null,
    getProductById: null,
    validateProductForm: null,
    getAllProducts: null,
    getAllBrands: null,
    getAllTags: null,
    getAllProductTypes: null
  };

  private constructor () {}

  /**
   * Get singleton instance
   * @returns ProductFactory instance
   */
  static getInstance (): ProductFactory {
    if (!ProductFactory.instance) {
      ProductFactory.instance = new ProductFactory();
    }
    return ProductFactory.instance;
  }

  /**
   * Get product repository
   * @returns ProductRepository instance
   */
  getProductRepository (): ProductRepository {
    if (!this.productRepository) {
      this.productRepository = new ProductRepositoryImpl(ProductAPIDataSourceCombined);
    }
    return this.productRepository;
  }

  /**
   * Get create product use case
   * @returns CreateProductUseCase instance
   */
  getCreateProductUseCase (): CreateProductUseCase {
    if (!this.useCases.createProduct) {
      const repository = this.getProductRepository();
      this.useCases.createProduct = new CreateProductUseCase(repository);
    }
    return this.useCases.createProduct;
  }

  /**
   * Get update product use case
   * @returns UpdateProductUseCase instance
   */
  getUpdateProductUseCase (): UpdateProductUseCase {
    if (!this.useCases.updateProduct) {
      const repository = this.getProductRepository();
      this.useCases.updateProduct = new UpdateProductUseCase(repository);
    }
    return this.useCases.updateProduct;
  }

  /**
   * Get delete product use case
   * @returns DeleteProductUseCase instance
   */
  getDeleteProductUseCase (): DeleteProductUseCase {
    if (!this.useCases.deleteProduct) {
      const repository = this.getProductRepository();
      this.useCases.deleteProduct = new DeleteProductUseCase(repository);
    }
    return this.useCases.deleteProduct;
  }

  /**
   * Get product by ID use case
   * @returns GetProductByIdUseCase instance
   */
  getGetProductByIdUseCase (): GetProductByIdUseCase {
    if (!this.useCases.getProductById) {
      const repository = this.getProductRepository();
      this.useCases.getProductById = new GetProductByIdUseCase(repository);
    }
    return this.useCases.getProductById;
  }

  /**
   * Get validate product form use case
   * @returns ValidateProductFormUseCase instance
   */
  getValidateProductFormUseCase (): ValidateProductFormUseCase {
    if (!this.useCases.validateProductForm) {
      this.useCases.validateProductForm = new ValidateProductFormUseCase();
    }
    return this.useCases.validateProductForm;
  }

  /**
   * Get all products use case
   * @returns GetAllProductsUseCase instance
   */
  getGetAllProductsUseCase (): GetAllProductsUseCase {
    if (!this.useCases.getAllProducts) {
      const repository = this.getProductRepository();
      this.useCases.getAllProducts = new GetAllProductsUseCase(repository);
    }
    return this.useCases.getAllProducts;
  }

  /**
   * Get all brands use case
   * @returns GetAllBrandsUseCase instance
   */
  getGetAllBrandsUseCase (): GetAllBrandsUseCase {
    if (!this.useCases.getAllBrands) {
      const repository = this.getProductRepository();
      this.useCases.getAllBrands = new GetAllBrandsUseCase(repository);
    }
    return this.useCases.getAllBrands;
  }

  /**
   * Get all tags use case
   * @returns GetAllTagsUseCase instance
   */
  getGetAllTagsUseCase (): GetAllTagsUseCase {
    if (!this.useCases.getAllTags) {
      const repository = this.getProductRepository();
      this.useCases.getAllTags = new GetAllTagsUseCase(repository);
    }
    return this.useCases.getAllTags;
  }

  /**
   * Get all product types use case
   * @returns GetAllProductTypesUseCase instance
   */
  getGetAllProductTypesUseCase (): GetAllProductTypesUseCase {
    if (!this.useCases.getAllProductTypes) {
      const repository = this.getProductRepository();
      this.useCases.getAllProductTypes = new GetAllProductTypesUseCase(repository);
    }
    return this.useCases.getAllProductTypes;
  }

  /**
   * Get all use cases
   * @returns Object containing all use cases
   */
  getAllUseCases () {
    return {
      createProduct: this.getCreateProductUseCase(),
      updateProduct: this.getUpdateProductUseCase(),
      deleteProduct: this.getDeleteProductUseCase(),
      getProductById: this.getGetProductByIdUseCase(),
      validateProductForm: this.getValidateProductFormUseCase(),
      getAllProducts: this.getGetAllProductsUseCase(),
      getAllBrands: this.getGetAllBrandsUseCase(),
      getAllTags: this.getGetAllTagsUseCase(),
      getAllProductTypes: this.getGetAllProductTypesUseCase()
    };
  }

  /**
   * Reset all instances (useful for testing)
   */
  reset (): void {
    this.productRepository = null;
    this.useCases = {
      createProduct: null,
      updateProduct: null,
      deleteProduct: null,
      getProductById: null,
      validateProductForm: null,
      getAllProducts: null,
      getAllBrands: null,
      getAllTags: null,
      getAllProductTypes: null
    };
  }
}

// Export singleton instance
export const productFactory = ProductFactory.getInstance();
